import axios, { AxiosError } from "axios";
import { logoutAPI } from "../apiRequests/auth/logout";
import { APP_URLS } from "../constants/appConstants";
import { getCurrentPath, saveRedirectPath } from "./redirectUtils";

type SessionEndReason = "revoked" | "logged_out";

// Query param that tells the sign-in page why the user is there.
const SESSION_ENDED_PARAM = "reason";
const SIGN_IN_PATH = `/${APP_URLS.SIGN_IN}`;

// Read once, when the page loads: the startup session check navigates to the
// sign-in page again without the query string, so by the time the page
// renders the param is gone from the URL.
export const SESSION_ENDED_REASON: string | null =
    window.location.pathname === SIGN_IN_PATH
        ? new URLSearchParams(window.location.search).get(SESSION_ENDED_PARAM)
        : null;

let leaving = false;

// Leaves the app for the sign-in page after the Admin Access Manager ended this
// session (an administrator revoked it, or it was logged out in another tab).
// A full-page load, not router navigation, so nothing from the ended session
// stays in memory. Runs once, however many events or failing requests trigger it.
export const leaveAfterSessionEnded = async (reason: SessionEndReason): Promise<void> => {
    if (leaving || window.location.pathname === SIGN_IN_PATH) return;
    leaving = true;
    // Signing in again brings the user back to the page they were on (e.g. "/capa-management/2026-Q3").
    saveRedirectPath(getCurrentPath());
    // Clears the session cookie; a failure here doesn't matter, the session is
    // already refused on every request.
    await logoutAPI().catch(() => undefined);
    window.location.replace(`${SIGN_IN_PATH}?${SESSION_ENDED_PARAM}=${reason}`);
};

// Any API call refused because the session was ended (401 with `reason`, see
// the backend's SessionEndedException) leaves the app -- the fallback when the
// event stream below was missed. apiRequests/fetch hands out the global axios,
// so one interceptor covers every request.
export const installSessionEndedInterceptor = (): void => {
    axios.interceptors.response.use(undefined, (error: AxiosError<{ reason?: string }>) => {
        const reason = error?.response?.status === 401 ? error.response.data?.reason : undefined;
        if (reason === "revoked" || reason === "logged_out") {
            leaveAfterSessionEnded(reason);
        }
        return Promise.reject(error);
    });
};

// Listens for the admin app ending this session and leaves at once. The browser
// reconnects on its own after a network drop, and the admin app sends the event
// straight away on reconnect if the session ended meanwhile.
export const openSessionEndedStream = (adminEventsUrl: string): (() => void) => {
    const source = new EventSource(adminEventsUrl);
    source.addEventListener("session-ended", (event) => {
        source.close();
        let reason: SessionEndReason = "revoked";
        try {
            if (JSON.parse((event as MessageEvent).data).reason === "logged_out") reason = "logged_out";
        } catch (e) {
            // Malformed payload -- treat as revoked.
        }
        leaveAfterSessionEnded(reason);
    });
    return () => source.close();
};
